import { useState, useCallback, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import axios from "axios";

import { Modal } from "shared/ui";
import { getFullEntityName } from "shared/lib/helpers";
import { CalendarDateI } from "shared/ui/calendar-form";
import { combineDateTimeWithZone } from "shared/lib/helpers/date";
import {
  US_ZONE_NAME_TO_TZ_CODE,
  UsZoneName,
} from "shared/lib/constants/timezones";
import { TIME_INTERVALS } from "shared/lib/constants/time";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "shared/ui/shadcn/select";

import { useAccountsContext } from "@/hooks/use-accounts-context";
import { useUser } from "@/hooks/use-global-context";
import { glencocoClientAPI } from "@/api/glencoco";
import {
  SCHEDULING_LINK_MODAL_ID,
  INSUFFICIENT_OAUTH_SCOPES_ERROR_CODE,
} from "./constants";
import { dd } from "@/helpers/datadog";
import { ERROR_CATEGORIES } from "@/constants/errors";
import { handleErrorMessage } from "@/helpers/error";
import { CampaignI } from "@/lib/types/campaign";
import { useFetchAccountDetails } from "@/api/routes/account/queries";

import { useFetchLookalike } from "@/modules/pipeline/account-details/data/queries";

import {
  AutoSyncStep,
  VerifyingStep,
  ConfirmDetectedStep,
  FallbackStep,
  InstructionsStep,
} from "./components";
import {
  HomefixFormFieldsI,
  normalizeHomefixState,
} from "./components/fallback-step";
import { prefillSchedulingLink } from "shared/lib/helpers/scheduling";
import { useMutationUpdateBookedMeetingNotes } from "../data/mutations";
import { useLookalikeMeetingSync } from "./hooks/use-lookalike-meeting-sync";
import { useOAuthMeetingDetection } from "./hooks/use-oauth-meeting-detection";
import { DetectedMeetingI } from "./types";
import { resolveLookalikeDetectionOutcome } from "./utils/lookalike-meeting-sync";
import { resolveBookingAEId } from "./utils/resolve-booking-ae";

import { swallow } from "@/api/legacy-swallow";

const SCHEDULING_FORM_STEP_TYPES = {
  INSTRUCTIONS: "instructions",
  VERIFYING: "verifying",
  CONFIRM_DETECTED: "confirm_detected",
  FALLBACK: "fallback",
  AUTO_SYNC: "auto_sync",
} as const;

type StepType = ValueOfObjectFields<typeof SCHEDULING_FORM_STEP_TYPES>;

interface SchedulingLinkModalPropsI {
  campaign?: CampaignI;
  modalId?: string;
  // detectedMeeting is absent for the lookalike auto-sync path: the meeting is
  // created asynchronously by the ICS pipeline, so there is nothing to pass.
  onSuccess: (detectedMeeting?: DetectedMeetingI) => void;
  onClose?: () => void;
  isReschedule?: boolean;
  isAsyncBooking?: boolean;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = TIME_INTERVALS.ONE_SECOND * 2;

interface AvailableAE {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  scheduling_link: string;
}

interface DetectMeetingMultiAERequest {
  contact_email: string;
  ae_ids?: string[];
}

const ModalContent = ({
  isReschedule,
  isAsyncBooking,
  onSuccess,
}: Pick<
  SchedulingLinkModalPropsI,
  "campaign" | "isReschedule" | "isAsyncBooking" | "onSuccess"
>) => {
  const { modalAccount } = useAccountsContext();

  // Some entry points (Open Pipeline context-menu, kanban-board, status-section
  // actions) put the account into modalAccount without accountExecutive or
  // scheduling. Lazy-fetch account-details to backfill them. The query is
  // deduped via React Query under the same key useAccountDetails uses, so this
  // is a free cache hit when the modal is opened from the account-details page
  // and a single extra request when opened from list views.
  const needsAccountDetailsFallback =
    !modalAccount?.accountExecutive || !modalAccount?.scheduling;
  const accountDetailsFallbackQuery = useFetchAccountDetails(
    modalAccount?.campaignId,
    modalAccount?.accountId,
    needsAccountDetailsFallback
  );
  const fallbackAccountExecutive =
    accountDetailsFallbackQuery.data?.data?.account_executive ?? undefined;
  const fallbackScheduling =
    accountDetailsFallbackQuery.data?.data?.scheduling ?? undefined;

  const scheduling = modalAccount?.scheduling ?? fallbackScheduling;
  const assignedAE = modalAccount?.accountExecutive ?? fallbackAccountExecutive;

  // Lookalike campaigns sync meetings from the calendar invite itself (the
  // lookalike guest inbox) — the AE has no calendar connection by definition,
  // so calendar-API detection can never succeed there. Same hook/condition as
  // the copy block in InstructionsStep; React Query dedupes the request.
  const { data: lookalike } = useFetchLookalike(modalAccount?.campaignId);
  const hasLookalike = !!lookalike?.email && lookalike.is_domain_verified;

  const supportsDirectScheduling =
    scheduling?.supports_direct_scheduling ?? false;
  const hasRoundRobinLink = !!scheduling?.round_robin_link;
  const hasDefaultAE = !!scheduling?.default_ae_id;

  // Check if account has an assigned AE (reschedule/rebooking case)
  const hasAssignedAE = !!assignedAE?.id && !!assignedAE?.scheduling_link;

  // Check if CASE 3 (no round robin, no default AE, no assigned AE)
  const isCase3 =
    (supportsDirectScheduling || isAsyncBooking) &&
    !hasRoundRobinLink &&
    !hasDefaultAE &&
    !hasAssignedAE;

  // Show AE selector in fallback step:
  //  - when round-robin detection fails (original case), or
  //  - on reschedule when no AE could be resolved (defense-in-depth: covers
  //    cases where the backend returns null AE or the fallback fetch fails).
  const showFallbackAESelector =
    (hasRoundRobinLink || !!isReschedule) && !hasAssignedAE;

  // Step state - always start with INSTRUCTIONS
  const [step, setStep] = useState<StepType>(
    SCHEDULING_FORM_STEP_TYPES.INSTRUCTIONS
  );

  // One availableAEs list feeds two pickers with opposite filters: the FALLBACK
  // one attributes an existing booking (scheduling link irrelevant), the CASE 3
  // one picks who to book through (link required). Both conditions can hold at
  // once (reschedule on a campaign with no round-robin and no default AE), so
  // only then does the live step decide; otherwise the mode is fixed for the
  // modal's lifetime, which keeps this to a single request.
  const isAttributionFetch =
    showFallbackAESelector &&
    (!isCase3 || step === SCHEDULING_FORM_STEP_TYPES.FALLBACK);

  // Lookalike campaigns: instead of claiming success on the SDR's click, watch
  // for the meeting the ICS pipeline syncs and report the real state. Enabled
  // from open (captures the pre-booking baseline); actively polls on AUTO_SYNC.
  const {
    status: lookalikeSyncStatus,
    meetingTime: lookalikeSyncedTime,
    activityLogId: lookalikeActivityLogId,
  } = useLookalikeMeetingSync({
    campaignId: modalAccount?.campaignId,
    accountId: modalAccount?.accountId,
    enabled: hasLookalike,
    active: step === SCHEDULING_FORM_STEP_TYPES.AUTO_SYNC,
  });

  // Second-tier fallback for lookalike bookings: while AutoSyncStep waits on the
  // ICS invite, also watch the AE's connected calendar (OAuth). Covers bookings
  // where the lookalike guest wasn't added or the provider can't carry one.
  const oauthDetection = useOAuthMeetingDetection({
    campaignId: modalAccount?.campaignId,
    accountId: modalAccount?.accountId,
    contactEmail: modalAccount?.contact?.email,
    aeId: assignedAE?.id,
    enabled: hasLookalike,
    // ICS wins once found, so polling the AE's calendar after that is wasted
    // while the SDR types notes.
    active:
      step === SCHEDULING_FORM_STEP_TYPES.AUTO_SYNC &&
      lookalikeSyncStatus !== "found",
  });

  // AE selection state (for CASE 3)
  const [availableAEs, setAvailableAEs] = useState<AvailableAE[]>([]);
  const [selectedAE, setSelectedAE] = useState<AvailableAE | null>(null);
  const [isLoadingAEs, setIsLoadingAEs] = useState(false);

  // Form state
  const [meetingTime, setMeetingTime] = useState<string>();
  // GLE-8478: the IANA zone the rep picked the time in. The card shows the
  // meeting in it.
  const [meetingTimezone, setMeetingTimezone] = useState<string>();
  const [detectedMeeting, setDetectedMeeting] =
    useState<DetectedMeetingI | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [notesError, setNotesError] = useState(false);
  const [homefixFields, setHomefixFields] = useState<HomefixFormFieldsI>({
    address: "",
    city: modalAccount?.contact?.person_city || "",
    state: normalizeHomefixState(modalAccount?.contact?.person_state),
    zip_code: modalAccount?.contact?.person_postal_code || "",
    product: "",
  });

  // Homefix "confirmed" toggle, kept separate from the string-keyed homefixFields.
  // Only reps with can_confirm_homefix can set it; the backend re-enforces the flag.
  const [confirmed, setConfirmed] = useState(false);
  const canConfirm = useUser()?.can_confirm_homefix ?? false;

  // UI state
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const { mutateAsync: updateBookedMeetingNotes, isPending: isSavingNotes } =
    useMutationUpdateBookedMeetingNotes();

  // Combine the two lookalike detection channels. ICS wins if both land; OAuth
  // is the fallback (see resolveLookalikeDetectionOutcome).
  const lookalikeDetectionOutcome = resolveLookalikeDetectionOutcome({
    icsStatus: lookalikeSyncStatus,
    oauthFound: oauthDetection.found,
  });

  // OAuth found the meeting on the AE's calendar before the ICS invite synced.
  // The event isn't persisted yet, so hand off to the existing confirm-then-book
  // step rather than closing on it like the ICS path.
  useEffect(() => {
    if (
      step === SCHEDULING_FORM_STEP_TYPES.AUTO_SYNC &&
      lookalikeDetectionOutcome === "oauth_found" &&
      oauthDetection.detectedMeeting
    ) {
      dd.rum.log("lookalike_detect.oauth_handoff", {
        campaign_id: modalAccount?.campaignId,
        account_id: modalAccount?.accountId,
        tier: "oauth",
      });
      setDetectedMeeting(oauthDetection.detectedMeeting);
      setStep(SCHEDULING_FORM_STEP_TYPES.CONFIRM_DETECTED);
    }
  }, [
    step,
    lookalikeDetectionOutcome,
    oauthDetection.detectedMeeting,
    modalAccount?.campaignId,
    modalAccount?.accountId,
  ]);

  // Fetch available AEs for CASE 3, round-robin fallback, or the reschedule
  // defense-in-depth selector (when isReschedule but no AE could be resolved).
  useEffect(() => {
    if ((isCase3 || showFallbackAESelector) && modalAccount?.campaignId) {
      // The two modes write the same state, so a superseded response must not
      // land: without this a slow booking-mode fetch can overwrite the
      // attribution list and silently re-hide the link-less AEs.
      let superseded = false;

      setIsLoadingAEs(true);
      const API = glencocoClientAPI();

      swallow(
        API.scheduling.getAvailableAEs(
          modalAccount.campaignId,
          isAttributionFetch
        )
      )
        .then((response) => {
          if (!superseded && response.data.aes) {
            setAvailableAEs(response.data.aes);
          }
        })
        .catch((error) => {
          if (superseded) {
            return;
          }

          toast.error("Failed to load available AEs");
          dd.rum.error(
            `${ERROR_CATEGORIES.BOOK_MEETING} - failed to fetch available AEs`,
            { error }
          );
        })
        .finally(() => {
          if (!superseded) {
            setIsLoadingAEs(false);
          }
        });

      return () => {
        superseded = true;
      };
    }
  }, [
    isCase3,
    showFallbackAESelector,
    isAttributionFetch,
    modalAccount?.campaignId,
  ]);

  // Derived values
  // Priority: 1) Assigned AE (reschedule), 2) Selected AE (CASE 3 / reschedule fallback),
  //           3) Default AE, 4) Whatever assigned AE we have (may lack scheduling_link).
  const accountExecutive = hasAssignedAE
    ? assignedAE
    : selectedAE
      ? {
          id: selectedAE.id,
          first_name: selectedAE.first_name,
          last_name: selectedAE.last_name,
          email: selectedAE.email,
          scheduling_link: selectedAE.scheduling_link,
        }
      : hasDefaultAE && scheduling?.default_ae_id
        ? {
            id: scheduling.default_ae_id,
            first_name: "",
            last_name: "",
            email: "",
            scheduling_link: scheduling.default_ae_link || "",
          }
        : assignedAE;

  // Determine scheduling link:
  // Priority: 1) Assigned AE (reschedule), 2) Round robin, 3) Default AE, 4) Selected AE (CASE 3)
  const schedulingLink = useMemo(() => {
    if (hasAssignedAE) {
      return assignedAE?.scheduling_link;
    }

    if (hasRoundRobinLink) {
      return scheduling?.round_robin_link;
    }

    if (hasDefaultAE) {
      return scheduling?.default_ae_link;
    }

    return selectedAE?.scheduling_link || null;
  }, [
    hasAssignedAE,
    assignedAE?.scheduling_link,
    hasRoundRobinLink,
    scheduling?.round_robin_link,
    hasDefaultAE,
    scheduling?.default_ae_link,
    selectedAE?.scheduling_link,
  ]);

  const aeName = getFullEntityName(accountExecutive);

  // Get prefilled scheduling link with prospect data
  const prefilledSchedulingLink = schedulingLink
    ? prefillSchedulingLink(schedulingLink, {
        name: modalAccount?.contact?.name,
        email: modalAccount?.contact?.email,
        // Pre-invite the lookalike mailbox as a guest so the calendar invite
        // reaches it automatically and the ICS pipeline can sync the meeting.
        // Only cal.com / Calendly honor a guest URL param; other providers
        // ignore it and the SDR falls back to the manual "add as guest" step.
        guests:
          hasLookalike && lookalike?.email ? [lookalike.email] : undefined,
      })
    : null;

  // Both booking steps gate their "open the scheduling link" affordance on
  // this; handleOpenSchedulingLink is a no-op without a resolved URL.
  const canOpenSchedulingLink = !!prefilledSchedulingLink;

  // Handlers
  const handleOpenSchedulingLink = useCallback(() => {
    if (prefilledSchedulingLink) {
      const width = 800;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        prefilledSchedulingLink,
        "scheduling_link_popup",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      // If popup was blocked, open in new tab as fallback
      if (!popup || popup.closed || typeof popup.closed === "undefined") {
        window.open(prefilledSchedulingLink, "_blank");
      }
    }
  }, [prefilledSchedulingLink]);

  const handleSelectAE = useCallback(
    (aeId: string) => {
      const ae = availableAEs.find((a) => a.id === aeId);
      if (ae) {
        setSelectedAE(ae);
      }
    },
    [availableAEs]
  );

  const handleVerifyBooking = useCallback(async () => {
    // For async booking (Homefix), skip calendar detection — go straight to
    // manual time entry so the meeting is created in our system and submitted
    // to LeadConduit/ActiveProspect.
    //
    // NOTE: This skip means handleConfirmDetectedMeeting never fires for async
    // campaigns, which is why it doesn't include Homefix form fields. If async
    // booking ever allows calendar detection, that path will need Homefix fields too.
    if (isAsyncBooking) {
      setStep(SCHEDULING_FORM_STEP_TYPES.FALLBACK);
      return;
    }

    // Lookalike campaigns: hand off to AutoSyncStep, which watches two channels
    // in parallel — the ICS invite the lookalike guest receives (primary) and,
    // after a short delay, the AE's connected calendar via OAuth (fallback for
    // when no guest was added). Manual entry stays available throughout. Don't
    // run the inline detect loop below (it would race those channels).
    if (hasLookalike) {
      setStep(SCHEDULING_FORM_STEP_TYPES.AUTO_SYNC);
      return;
    }

    // For round robin, we don't need accountExecutive (backend handles routing)
    // For other cases, we need accountExecutive ID
    if (
      !modalAccount?.contact?.email ||
      (!hasRoundRobinLink && !accountExecutive?.id)
    ) {
      // Suppress the "missing AE" toast while the lazy account-details fetch
      // is still in flight — FallbackStep will gate the manual CTA until it
      // resolves. We still drop into FALLBACK so the user can pick a time.
      const isStillResolvingAE =
        !accountExecutive?.id &&
        needsAccountDetailsFallback &&
        accountDetailsFallbackQuery.isLoading;

      if (!isStillResolvingAE) {
        toast.error("Missing contact email or AE information");
      }

      setStep(SCHEDULING_FORM_STEP_TYPES.FALLBACK);
      return;
    }

    setStep(SCHEDULING_FORM_STEP_TYPES.VERIFYING);

    const API = glencocoClientAPI();
    let found = false;
    let detected: DetectedMeetingI | null = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }

      try {
        let response;

        if (supportsDirectScheduling) {
          // NEW: Use multi-AE detection for direct scheduling campaigns
          const requestBody: DetectMeetingMultiAERequest = {
            contact_email: modalAccount.contact.email,
          };

          // Priority: 1) Assigned AE (reschedule), 2) Selected AE (CASE 3)
          if (hasAssignedAE && assignedAE?.id) {
            // Reschedule: query assigned AE's calendar
            requestBody.ae_ids = [assignedAE.id];
          } else if (selectedAE) {
            // CASE 3: query user-selected AE's calendar
            requestBody.ae_ids = [selectedAE.id];
          }
          // Otherwise: backend will handle round robin (CASE 1) or default AE (CASE 2) routing

          response = await swallow(
            API.scheduling.detectMeetingMultiAE(
              modalAccount.campaignId,
              modalAccount.accountId,
              requestBody
            )
          );
        } else {
          // LEGACY: Use single-AE detection for Calendly campaigns
          if (!accountExecutive?.id) {
            throw new Error("Missing AE information for legacy detection");
          }

          response = await swallow(
            API.scheduling.detectMeetingFromCalendar(
              modalAccount.campaignId,
              modalAccount.accountId,
              {
                contact_email: modalAccount.contact.email,
                ae_id: accountExecutive.id,
              }
            )
          );
        }

        // Handle AxiosError returned (not thrown) by the response interceptor
        if (axios.isAxiosError(response)) {
          const errorCode = response.response?.data?.error_code;

          if (
            response.response?.status === 403 &&
            errorCode === INSUFFICIENT_OAUTH_SCOPES_ERROR_CODE
          ) {
            toast.error(
              `${
                aeName || "The AE"
              } hasn't granted calendar access. Please enter the meeting details manually.`
            );
            dd.rum.error(
              `${ERROR_CATEGORIES.BOOK_MEETING} - insufficient OAuth scopes, skipping retries`,
              { error: response }
            );
            break;
          }

          dd.rum.error(
            `${ERROR_CATEGORIES.BOOK_MEETING} - detect meeting attempt ${
              attempt + 1
            } failed`,
            { error: response }
          );
          continue;
        }

        if (response.data.found && response.data.meeting_time) {
          found = true;

          detected = {
            ae_id: response.data.ae_id, // Use detected AE (may differ from accountExecutive.id in multi-AE scenarios)
            meeting_time: response.data.meeting_time,
            event_id: response.data.event_id,
            attendees: response.data.attendees,
          };

          break;
        }
      } catch (error) {
        dd.rum.error(
          `${ERROR_CATEGORIES.BOOK_MEETING} - detect meeting attempt ${
            attempt + 1
          } failed`,
          { error }
        );
      }
    }

    if (found && detected) {
      setDetectedMeeting(detected);
      setStep(SCHEDULING_FORM_STEP_TYPES.CONFIRM_DETECTED);
    } else {
      setStep(SCHEDULING_FORM_STEP_TYPES.FALLBACK);
    }
  }, [
    modalAccount,
    accountExecutive,
    assignedAE,
    hasAssignedAE,
    hasRoundRobinLink,
    selectedAE,
    supportsDirectScheduling,
    isAsyncBooking,
    hasLookalike,
    isReschedule,
    onSuccess,
    aeName,
    needsAccountDetailsFallback,
    accountDetailsFallbackQuery.isLoading,
  ]);

  const handleDateChange = useCallback(
    ({ date, time, timezone }: CalendarDateI) => {
      if (!time) return;

      setMeetingTime(
        combineDateTimeWithZone(date, time, timezone).toISOString()
      );
      setMeetingTimezone(US_ZONE_NAME_TO_TZ_CODE[timezone as UsZoneName]);
    },
    []
  );

  const handleNotesChange = useCallback((value: string) => {
    setNotes(value);
    if (value.trim()) {
      setNotesError(false);
    }
  }, []);

  const handleHomefixFieldChange = useCallback(
    (field: keyof HomefixFormFieldsI, value: string) => {
      setHomefixFields((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleBookMeetingManually = useCallback(async () => {
    if (!meetingTime) {
      toast.error("Please select a meeting time");
      return;
    }

    if (!accountExecutive?.id) {
      toast.error("No account executive assigned. Please contact support.");
      return;
    }

    if (!modalAccount?.contact?.contact_id) {
      toast.error("Missing contact information");
      return;
    }

    if (isAsyncBooking) {
      if (
        !homefixFields.address.trim() ||
        !homefixFields.city.trim() ||
        !homefixFields.state ||
        !homefixFields.zip_code.trim() ||
        !homefixFields.product
      ) {
        toast.error("Please fill in all required fields");
        return;
      }

      // Backend normalizes to the 5-digit base (ZIP+4 accepted) and rejects
      // anything outside the Homefix service area; catch the format here so the
      // SDR gets an actionable message before the round-trip.
      if (!/^\d{5}(-\d{4})?$/.test(homefixFields.zip_code.trim())) {
        toast.error("Enter a valid 5-digit zip code");
        return;
      }

      if (!notes.trim()) {
        toast.error("Notes are required for this booking");
        return;
      }
    }

    setIsBookingLoading(true);

    const API = glencocoClientAPI();
    const bookingParams = {
      account_executive_id: accountExecutive.id,
      meeting_time: meetingTime,
      meeting_timezone: meetingTimezone,
      notes,
      ...(isAsyncBooking && {
        address: homefixFields.address,
        city: homefixFields.city,
        state: homefixFields.state,
        zip_code: homefixFields.zip_code,
        product: homefixFields.product,
        comments: notes,
        confirmed,
      }),
    };

    try {
      const response = isReschedule
        ? await swallow(
            API.scheduling.rescheduleMeetingWithTime(
              modalAccount.campaignId,
              modalAccount.accountId,
              modalAccount.contact.contact_id,
              bookingParams
            )
          )
        : await swallow(
            API.scheduling.bookMeetingWithTime(
              modalAccount.campaignId,
              modalAccount.accountId,
              modalAccount.contact.contact_id,
              bookingParams
            )
          );

      // Handle AxiosError returned (not thrown) by the response interceptor
      if (axios.isAxiosError(response)) {
        const errorCode = response.response?.data?.error_code;
        // Surface the backend's specific reason for bad-request failures (e.g.
        // "zip_code is outside the Homefix service area"); 1007 is a generic
        // bad-body code with no app-wide message, so without this the SDR would
        // only see the opaque default toast.
        const errorReason =
          response.response?.status === 400
            ? response.response?.data?.error_str
            : undefined;

        handleErrorMessage(errorCode, errorReason || "Failed to book meeting");
        dd.rum.error(
          `${ERROR_CATEGORIES.BOOK_MEETING} - manual booking failed`,
          { error: response }
        );

        return;
      }

      if (response.status === 200) {
        onSuccess({ meeting_time: meetingTime });
      } else {
        toast.error("Failed to book meeting");
      }
    } catch (error) {
      toast.error("Failed to book meeting");
      dd.rum.error(`${ERROR_CATEGORIES.BOOK_MEETING} - manual booking failed`, {
        error,
      });
    } finally {
      setIsBookingLoading(false);
    }
  }, [
    meetingTime,
    meetingTimezone,
    notes,
    modalAccount,
    accountExecutive,
    isReschedule,
    isAsyncBooking,
    homefixFields,
    onSuccess,
  ]);

  const handleConfirmDetectedMeeting = useCallback(async () => {
    if (!notes.trim()) {
      setNotesError(true);
      return;
    }

    const acceptableAEId = resolveBookingAEId({
      detectedAEId: detectedMeeting?.ae_id,
      assignedAEId: accountExecutive?.id,
    });

    if (!detectedMeeting?.meeting_time || !acceptableAEId) {
      toast.error("Missing meeting information");
      return;
    }

    if (!modalAccount?.contact?.contact_id) {
      toast.error("Missing contact information");
      return;
    }

    setIsBookingLoading(true);

    const API = glencocoClientAPI();
    const bookingParams = {
      // Use detected AE ID if available (multi-AE detection), otherwise fall back to original AE
      account_executive_id: acceptableAEId,
      meeting_time: detectedMeeting.meeting_time,
      notes,
      calendar_event_id: detectedMeeting.event_id,
      attendees: detectedMeeting.attendees,
    };

    try {
      const response = isReschedule
        ? await swallow(
            API.scheduling.rescheduleMeetingWithTime(
              modalAccount.campaignId,
              modalAccount.accountId,
              modalAccount.contact.contact_id,
              bookingParams
            )
          )
        : await swallow(
            API.scheduling.bookMeetingWithTime(
              modalAccount.campaignId,
              modalAccount.accountId,
              modalAccount.contact.contact_id,
              bookingParams
            )
          );

      // Handle AxiosError returned (not thrown) by the response interceptor
      if (axios.isAxiosError(response)) {
        const errorCode = response.response?.data?.error_code;
        // Surface the backend's specific reason for bad-request failures (e.g.
        // "zip_code is outside the Homefix service area"); 1007 is a generic
        // bad-body code with no app-wide message, so without this the SDR would
        // only see the opaque default toast.
        const errorReason =
          response.response?.status === 400
            ? response.response?.data?.error_str
            : undefined;

        handleErrorMessage(errorCode, errorReason || "Failed to book meeting");
        dd.rum.error(
          `${ERROR_CATEGORIES.BOOK_MEETING} - detected meeting booking failed`,
          { error: response }
        );

        return;
      }

      if (response.status === 200) {
        onSuccess(detectedMeeting);
      } else {
        toast.error("Failed to book meeting");
      }
    } catch (error) {
      toast.error("Failed to book meeting");
      dd.rum.error(
        `${ERROR_CATEGORIES.BOOK_MEETING} - detected meeting booking failed`,
        { error }
      );
    } finally {
      setIsBookingLoading(false);
    }
  }, [
    detectedMeeting,
    notes,
    modalAccount,
    accountExecutive,
    isReschedule,
    onSuccess,
  ]);

  // GLE-8421: AutoSyncStep's Done. The ICS pipeline already created the
  // meeting; this only attaches the notes, by id, so it can't book a second one.
  const handleAutoSyncDone = useCallback(async () => {
    if (!modalAccount?.campaignId || !modalAccount?.accountId) {
      return;
    }

    if (!lookalikeActivityLogId) {
      // A backend without GLE-8421 sends no id: fail loudly rather than close
      // the modal with the notes unsaved.
      toast.error(
        "Couldn't find the synced meeting to attach notes to. Please refresh and try again."
      );
      dd.rum.error(
        `${ERROR_CATEGORIES.BOOK_MEETING} - auto-sync done with no activity_log_id`,
        {
          campaign_id: modalAccount.campaignId,
          account_id: modalAccount.accountId,
        }
      );

      return;
    }

    try {
      await updateBookedMeetingNotes({
        campaignId: modalAccount.campaignId,
        accountId: modalAccount.accountId,
        activityLogId: lookalikeActivityLogId,
        notes,
      });
    } catch (error) {
      const errorCode = axios.isAxiosError(error)
        ? error.response?.data?.error_code
        : undefined;
      handleErrorMessage(errorCode, "Failed to save notes");
      dd.rum.error(
        `${ERROR_CATEGORIES.BOOK_MEETING} - auto-sync notes save failed`,
        { error }
      );

      return;
    }

    dd.rum.log("lookalike_detect.notes.submitted", {
      campaign_id: modalAccount.campaignId,
      account_id: modalAccount.accountId,
      activity_log_id: lookalikeActivityLogId,
    });

    onSuccess();
  }, [
    modalAccount,
    lookalikeActivityLogId,
    notes,
    updateBookedMeetingNotes,
    onSuccess,
  ]);

  if (step === SCHEDULING_FORM_STEP_TYPES.AUTO_SYNC) {
    return (
      <AutoSyncStep
        status={lookalikeSyncStatus}
        meetingTime={lookalikeSyncedTime}
        isReschedule={isReschedule}
        lookalikeEmail={lookalike?.email}
        notes={notes}
        onNotesChange={handleNotesChange}
        isSavingNotes={isSavingNotes}
        onDone={handleAutoSyncDone}
        onEnterManually={() => {
          dd.rum.log("lookalike_detect.manual_fallback", {
            campaign_id: modalAccount?.campaignId,
            account_id: modalAccount?.accountId,
          });
          setStep(SCHEDULING_FORM_STEP_TYPES.FALLBACK);
        }}
      />
    );
  }

  if (step === SCHEDULING_FORM_STEP_TYPES.VERIFYING) {
    return <VerifyingStep isReschedule={isReschedule} />;
  }

  // Hold the primary CTA until the lazy account-details fetch resolves so the
  // SDR can't submit before we know the AE.
  const isResolvingAccountDetails =
    needsAccountDetailsFallback && accountDetailsFallbackQuery.isLoading;

  if (step === SCHEDULING_FORM_STEP_TYPES.CONFIRM_DETECTED && detectedMeeting) {
    return (
      <ConfirmDetectedStep
        detectedMeeting={detectedMeeting}
        contactName={modalAccount?.contact?.name}
        accountName={modalAccount?.accountName}
        aeName={aeName}
        notes={notes}
        notesError={notesError}
        isLoading={isBookingLoading || isResolvingAccountDetails}
        isReschedule={isReschedule}
        onNotesChange={handleNotesChange}
        onConfirm={handleConfirmDetectedMeeting}
      />
    );
  }

  if (step === SCHEDULING_FORM_STEP_TYPES.FALLBACK) {
    return (
      <FallbackStep
        aeName={aeName}
        isLoading={isBookingLoading || isResolvingAccountDetails}
        isReschedule={isReschedule}
        isAsyncBooking={isAsyncBooking}
        onRetry={handleVerifyBooking}
        onDateChange={handleDateChange}
        initialNotes={notes}
        onNotesChange={handleNotesChange}
        handleOpenSchedulingLink={handleOpenSchedulingLink}
        canOpenSchedulingLink={canOpenSchedulingLink}
        onBookManually={handleBookMeetingManually}
        showAESelector={showFallbackAESelector}
        availableAEs={showFallbackAESelector ? availableAEs : undefined}
        selectedAE={showFallbackAESelector ? selectedAE : undefined}
        onSelectAE={showFallbackAESelector ? handleSelectAE : undefined}
        isLoadingAEs={isLoadingAEs}
        homefixFields={isAsyncBooking ? homefixFields : undefined}
        onHomefixFieldChange={
          isAsyncBooking ? handleHomefixFieldChange : undefined
        }
        canConfirm={isAsyncBooking ? canConfirm : undefined}
        confirmed={confirmed}
        onConfirmedChange={setConfirmed}
      />
    );
  }

  return (
    <>
      {supportsDirectScheduling && !isAsyncBooking && !hasLookalike && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-start gap-3">
            <svg
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
              fill="none"
              strokeWidth="2"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1 space-y-1">
              <h4 className="text-green-800 typography-body-4-semibold">
                New Direct Scheduling Flow
              </h4>
              <p className="text-green-700 typography-body-5">
                This campaign uses calendar-based meeting detection. After
                booking through the scheduling link, we'll automatically detect
                the meeting in the AE's calendar and confirm the booking.
              </p>
            </div>
          </div>
        </div>
      )}

      {isCase3 && (
        <div className="mb-6 space-y-2">
          <label className="text-gray-700 typography-body-4-medium">
            Select an account executive:
          </label>

          {isLoadingAEs ? (
            <div className="flex items-center justify-center py-4">
              <div className="text-gray-500 typography-body-4">
                Loading available AEs...
              </div>
            </div>
          ) : availableAEs.length === 0 ? (
            <div className="text-red-600 typography-body-4">
              No AEs with calendar connections and scheduling links are
              available.
            </div>
          ) : (
            <Select value={selectedAE?.id || ""} onValueChange={handleSelectAE}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose an AE to book with...">
                  {selectedAE &&
                    `${getFullEntityName(selectedAE)} (${selectedAE.email})`}
                </SelectValue>
              </SelectTrigger>

              <SelectContent className="z-[1000] max-h-[300px]">
                {availableAEs.map((ae) => (
                  <SelectItem key={ae.id} value={ae.id}>
                    <div>
                      <div className="typography-body-4-medium">
                        {getFullEntityName(ae)}
                      </div>
                      <div className="text-gray-600 typography-body-5">
                        {ae.email}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {(!isCase3 || (isCase3 && selectedAE !== null)) && (
        <InstructionsStep
          schedulingLink={schedulingLink}
          canOpenSchedulingLink={canOpenSchedulingLink}
          isReschedule={isReschedule}
          campaignId={modalAccount?.campaignId}
          onOpenSchedulingLink={handleOpenSchedulingLink}
          onVerifyBooking={handleVerifyBooking}
        />
      )}
    </>
  );
};

export const SchedulingLinkModal = ({
  campaign,
  isReschedule,
  isAsyncBooking,
  modalId = SCHEDULING_LINK_MODAL_ID,
  onSuccess,
  onClose,
}: SchedulingLinkModalPropsI) => (
  <Modal
    id={modalId}
    shouldCloseOnOverlayClick={false}
    showCloseButton
    modalBoxClassName="overflow-y-auto max-w-lg"
    title={
      isReschedule
        ? "Reschedule meeting via link"
        : "Book meeting via scheduling link"
    }
    titleClassName="typography-header-6 mb-6"
    unmountOnClose
    onClose={onClose}
  >
    <ModalContent
      campaign={campaign}
      isReschedule={isReschedule}
      isAsyncBooking={isAsyncBooking}
      onSuccess={onSuccess}
    />
  </Modal>
);

export { SCHEDULING_LINK_MODAL_ID };
